import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  IconCircleCheck,
  IconAlertCircle,
  IconLoader2
} from '@tabler/icons-react';
import { authAPI, billingAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [message, setMessage] = useState('Проверяю статус платежа...');
  const { user } = useAuth();

  useEffect(() => {
    const checkPayment = async () => {
      try {
        // Получаем payment_id из localStorage
        const paymentId = localStorage.getItem('pending_payment_id');
        
        if (!paymentId) {
          setStatus('error');
          setMessage('ID платежа не найден. Обратитесь в Telegram поддержку @XNCsup_bot');
          return;
        }

        // Задержка для обработки платежа
        await new Promise(r => setTimeout(r, 2000));

        // Проверяем статус платежа через API YooKassa
        const result = await billingAPI.checkPaymentStatus(paymentId);
        console.log('Payment status check:', result);

        if (result.paid) {
          // Очищаем сохранённый payment_id
          localStorage.removeItem('pending_payment_id');
          setStatus('success');
          setMessage('Спасибо за покупку! Ваш план обновлён на PRO.');
        } else {
          setStatus('error');
          setMessage('Платёж ещё не обработан. Попробуйте обновить страницу.');
        }
      } catch (e) {
        console.error('Payment check error:', e);
        setStatus('error');
        setMessage(e.message || 'Ошибка при проверке платежа. Обновите страницу.');
      }
    };

    checkPayment();
  }, []);

  return (
    <div className="min-h-screen bg-dark-950 flex items-center justify-center px-6 pt-32 pb-20">
      <div className="max-w-lg w-full">
        {/* Успешный платёж */}
        {status === 'success' && (
          <div className="rounded-2xl border border-dark-800 bg-dark-900 p-10 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <IconCircleCheck className="w-8 h-8 text-emerald-400" />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-white mb-3">
              Платёж успешен!
            </h1>

            <p className="text-dark-500 mb-6">
              {message}
            </p>

            <div className="bg-dark-950 border border-dark-800 rounded-xl p-4 mb-6 text-left">
              <p className="text-sm text-dark-500 mb-2">
                <span className="font-semibold text-white">Ваш статус:</span>
              </p>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
                <span className="text-emerald-400 font-medium">PRO подписка активна</span>
              </div>
              <p className="text-xs text-dark-500 mt-3">
                Теперь вы можете создать до 20 туннелей
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/dashboard"
                className="px-6 py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-semibold transition-colors"
              >
                К панели управления
              </Link>
              <Link
                to="/"
                className="px-6 py-3 rounded-lg border border-dark-800 bg-dark-850 text-white hover:border-dark-700 transition-colors"
              >
                На главную
              </Link>
            </div>
          </div>
        )}

        {/* Проверка статуса */}
        {status === 'checking' && (
          <div className="rounded-2xl border border-dark-800 bg-dark-900 p-10 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-xl bg-primary-500/10 flex items-center justify-center">
                <IconLoader2 className="w-8 h-8 text-primary-500 animate-spin" />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-white mb-3">
              Обработка платежа...
            </h1>

            <p className="text-dark-500 mb-6">
              {message}
            </p>

            <p className="text-dark-500 text-sm">
              Это займёт несколько секунд. Пожалуйста, не закрывайте страницу.
            </p>
          </div>
        )}

        {/* Ошибка */}
        {status === 'error' && (
          <div className="rounded-2xl border border-dark-800 bg-dark-900 p-10 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <IconAlertCircle className="w-8 h-8 text-amber-400" />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-white mb-3">
              Проблема с обновлением
            </h1>

            <p className="text-amber-400 mb-6">
              {message}
            </p>

            <div className="bg-dark-950 border border-dark-800 rounded-xl p-4 mb-6 text-left text-sm text-dark-500">
              <p className="font-semibold text-white mb-2">Что делать:</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Обновите страницу (F5)</li>
                <li>Проверьте вашу подписку в панели управления</li>
                <li>Если проблема сохранится, свяжитесь с <a href="https://t.me/XNCsup_bot" target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:text-primary-400 underline">Telegram поддержкой</a></li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-semibold transition-colors"
              >
                Обновить страницу
              </button>
              <Link
                to="/dashboard"
                className="px-6 py-3 rounded-lg border border-dark-800 bg-dark-850 text-white hover:border-dark-700 transition-colors"
              >
                К панели управления
              </Link>
            </div>
          </div>
        )}

        {/* Дополнительная информация */}
        <div className="mt-6 text-center">
          <p className="text-dark-500 text-sm">
            Если платёж не прошёл, свяжитесь с{' '}
            <a
              href="https://t.me/XNCsup_bot"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-500 hover:text-primary-400 underline"
            >
              Telegram поддержкой
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
